// src/services/reportReminderSchedule.js
//
// When the "submit your weekly report" pop-up shows for coordinators.
//
//   - Every Friday.
//   - On the last working weekday (Mon–Fri) of the month, because goal
//     progress restarts on the 1st: the days of a week that fall in this month
//     must be reported before the month ends. When that day is a Friday the
//     two reminders are one pop-up ("both"). Holidays are not accounted for.
//
// Staff can snooze for an hour, snooze until 4 PM (offered only before 4 PM),
// or dismiss it for the day ("I've already submitted"). "Submit now" opens the
// form and snoozes for an hour, so it comes back if they leave without
// submitting. It always shows on these days, even after a report was filed.
//
// Pure and Firebase-free so it is unit-tested (reportReminderSchedule.test.js).
// Time is always passed in (`now`: a Date or epoch ms), in the browser's local
// time zone.

export const CUTOFF_HOUR = 16; // 4 PM: after this only the 1-hour snooze is offered
export const HOUR_MS = 60 * 60 * 1000;
const MAX_TIMEOUT_MS = 2147483647; // setTimeout's ceiling (~24.8 days)

const toDate = (now) => (now instanceof Date ? now : new Date(now));
const pad = (n) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" in local time — the day a saved choice belongs to. */
export const dayKey = (now) => {
  const d = toDate(now);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** The last Mon–Fri date of `now`'s month (local midnight). */
export const lastWorkingDayOfMonth = (now) => {
  const d = toDate(now);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0); // day 0 of next month
  while (last.getDay() === 0 || last.getDay() === 6) last.setDate(last.getDate() - 1);
  return last;
};

/** "friday" | "monthEnd" | "both" | null for the day `now` falls on. */
export const reminderKindFor = (now) => {
  const d = toDate(now);
  const friday = d.getDay() === 5;
  const monthEnd = dayKey(lastWorkingDayOfMonth(d)) === dayKey(d);
  if (friday && monthEnd) return "both";
  if (friday) return "friday";
  if (monthEnd) return "monthEnd";
  return null;
};

/**
 * The reminder kind to use, with the staging-only testing switch: when
 * `everyDay` is on, an ordinary day counts as a reminder day ("test").
 */
export const effectiveReminderKind = (now, { everyDay = false } = {}) =>
  reminderKindFor(now) || (everyDay ? "test" : null);

/** The snooze buttons to offer right now. */
export const snoozeChoices = (now) => {
  const d = toDate(now);
  const choices = [{ id: "hour", label: "Remind me in 1 hour" }];
  if (d.getHours() < CUTOFF_HOUR) choices.push({ id: "4pm", label: "Remind me at 4 PM" });
  return choices;
};

/** 4 PM today, as epoch ms. */
const fourPmToday = (now) => {
  const d = toDate(now);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), CUTOFF_HOUR, 0, 0, 0).getTime();
};

/** Local midnight at the start of tomorrow, as epoch ms. */
const nextMidnight = (now) => {
  const d = toDate(now);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0).getTime();
};

/**
 * Saved state is `{ dayKey, dismissed, snoozeUntil }`. A choice saved on an
 * earlier day never carries over, and anything malformed counts as no choice.
 */
const stateForToday = (state, now) =>
  state && typeof state === "object" && state.dayKey === dayKey(now) ? state : null;

/** Should the pop-up be on screen at `now`? (`opts.everyDay`: staging testing switch) */
export const shouldShow = (state, now, opts = {}) => {
  if (!effectiveReminderKind(now, opts)) return false;
  const today = stateForToday(state, now);
  if (!today) return true;
  if (today.dismissed === true) return false;
  const until = Number(today.snoozeUntil);
  if (Number.isFinite(until) && toDate(now).getTime() < until) return false;
  return true;
};

/**
 * The state after the person picks `choiceId`:
 *   "hour" | "submitNow" -> snooze 1 hour
 *   "4pm"                -> snooze until 4 PM today (falls back to 1 hour at/after 4 PM)
 *   "submitted"          -> hidden for the rest of today
 */
export const applyChoice = (state, choiceId, now) => {
  const t = toDate(now).getTime();
  const base = { dayKey: dayKey(now), dismissed: false, snoozeUntil: null };
  if (choiceId === "submitted") return { ...base, dismissed: true };
  if (choiceId === "4pm" && t < fourPmToday(now)) return { ...base, snoozeUntil: fourPmToday(now) };
  if (choiceId === "hour" || choiceId === "submitNow" || choiceId === "4pm") {
    return { ...base, snoozeUntil: t + HOUR_MS };
  }
  return stateForToday(state, now) || base;
};

/**
 * How long until the answer to `shouldShow` could next change: a snooze
 * ending today, or midnight (a new day may be a reminder day). Clamped to
 * what setTimeout accepts. The component also re-checks on focus, because a
 * sleeping laptop delays timers.
 */
export const msUntilNextCheck = (state, now) => {
  const t = toDate(now).getTime();
  let next = nextMidnight(now);
  const today = stateForToday(state, now);
  const until = Number(today?.snoozeUntil);
  if (today && !today.dismissed && Number.isFinite(until) && until > t && until < next) next = until;
  return Math.min(Math.max(next - t, 1000), MAX_TIMEOUT_MS);
};

/** The pop-up's heading and line for each kind of day. */
export const reminderCopy = (kind) => {
  if (kind === "both") {
    return {
      title: "It's Friday, and the last working day of the month!",
      body: "Please submit your weekly report today. Goal progress starts fresh on the 1st, so this month's numbers need to be in before then.",
    };
  }
  if (kind === "test") {
    return {
      title: "Weekly report reminder (test)",
      body: "This test version shows every day. Normally it only appears on Fridays and on the last working day of the month.",
    };
  }
  if (kind === "monthEnd") {
    return {
      title: "It's the last working day of the month!",
      body: "Goal progress starts fresh on the 1st. Please submit a report for this week's days so far, so they count toward this month.",
    };
  }
  return {
    title: "It's Friday!",
    body: "Don't forget to submit your weekly report.",
  };
};
